import { ImagePlus, Trash2 } from '@/components/ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { toast } from '@/stores/ui';

const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';

/**
 * Upload an image (shown as a circle, like avatars and server icons).
 * `onChange(blob)` receives the new image, or `null` to remove it.
 */
export function ImagePicker({
  currentUrl,
  fallback,
  onChange,
  allowRemove = true,
}: {
  currentUrl: string | null;
  fallback: React.ReactNode;
  onChange: (image: Blob | null) => void;
  allowRemove?: boolean;
}) {
  const [chosen, setChosen] = useState<Blob | null | undefined>(undefined);
  const input = useRef<HTMLInputElement>(null);
  const preview = useMemo(() => (chosen ? URL.createObjectURL(chosen) : null), [chosen]);
  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);

  const shown = chosen === undefined ? currentUrl : preview;

  const choose = (blob: Blob | null) => {
    setChosen(blob);
    onChange(blob);
  };

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-full border-2 border-dashed border-line-strong bg-sunken">
        {shown ? <img src={shown} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center">{fallback}</div>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" icon={<ImagePlus size={15} />} onClick={() => input.current?.click()}>
            Upload image
          </Button>
          {allowRemove && shown && (
            <Button variant="ghost" size="sm" icon={<Trash2 size={15} />} onClick={() => choose(null)}>
              Remove
            </Button>
          )}
        </div>
        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            if (!ACCEPT.split(',').includes(file.type)) return toast.error('Please choose a PNG, JPEG, GIF or WebP image.');
            if (file.size > 8 * 1024 * 1024) return toast.error('Images must be under 8 MB.');
            choose(file);
          }}
        />
      </div>
    </div>
  );
}
